import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, Save, Send, Download, Printer, Mail, Eye, EyeOff, FileText, Package,
} from "lucide-react";
import { toast } from "sonner";
import InvoicePreview from "@/components/invoicing/InvoicePreview";
import InvoiceItemPicker, { SaveAsTemplateButton } from "@/components/invoicing/InvoiceItemPicker";
import {
  downloadInvoicePDF, printInvoicePDF, downloadInvoiceTXT, generateBarcodeData,
} from "@/lib/invoicePdf";

const emptyItem = (vat = 25.5) => ({
  description: "",
  quantity: 1,
  unit: "kpl",
  unit_price: 0,
  vat_percent: vat,
  total: 0,
});

function computeItem(item) {
  const qty = Number(item.quantity) || 0;
  const price = Number(item.unit_price) || 0;
  const vat = Number(item.vat_percent) || 0;
  return { ...item, total: qty * price * (1 + vat / 100) };
}

const DRAFT_KEY = (id) => `apps2go.invoice_draft.${id || "new"}`;

export default function InvoiceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [settings, setSettings] = useState({});
  const [pickerForRow, setPickerForRow] = useState(null);  // rivinumero jolle nimike valitaan
  const [form, setForm] = useState({
    invoice_number: "",
    reference_number: "",
    sender_name: "",
    sender_business_id: "",
    sender_address: "",
    sender_email: "",
    sender_phone: "",
    sender_bank_account: "",
    sender_logo_url: "",
    recipient_name: "",
    recipient_business_id: "",
    recipient_address: "",
    recipient_email: "",
    items: [emptyItem()],
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "",
    notes: "",
    payment_terms: "14",
    payment_terms_days: 14,
    penalty_interest: 7,
    status: "draft",
    currency: "EUR",
  });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState("");

  // Load settings + existing invoice + autosaved draft
  useEffect(() => {
    (async () => {
      try {
        const sRes = await api.get("/settings/invoice");
        const s = sRes.data || {};
        setSettings(s);

        if (isEdit) {
          const inv = (await api.get(`/invoices/${id}`)).data;
          setForm((p) => ({
            ...p,
            ...inv,
            items: inv.items?.length ? inv.items : [emptyItem(s.default_vat_percent)],
            payment_terms: String(inv.payment_terms || inv.payment_terms_days || s.default_payment_terms || 14),
          }));
        } else {
          // Auto-detect next invoice + reference numbers from DB (server scans the highest)
          let nextInvoice = "";
          let nextRef = "";
          try {
            const nx = (await api.get("/invoices/next-number")).data;
            nextInvoice = nx.invoice_number;
            nextRef = nx.reference_number;
          } catch {
            /* ignore */
          }

          // Pre-fill from settings + check draft
          const draftRaw = localStorage.getItem(DRAFT_KEY("new"));
          if (draftRaw) {
            try {
              const draft = JSON.parse(draftRaw);
              setForm((p) => ({ ...p, ...draft }));
              toast.info("Tallennettu luonnos palautettu");
              return;
            } catch {
              /* ignore */
            }
          }
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + (s.default_payment_terms || 14));
          setForm((p) => ({
            ...p,
            invoice_number: nextInvoice,
            reference_number: nextRef,
            sender_name: s.company_name || s.sender_name || "",
            sender_business_id: s.business_id || s.sender_business_id || "",
            sender_address: [s.address, s.postal_code, s.city].filter(Boolean).join(", "),
            sender_email: s.email || s.sender_email || "",
            sender_phone: s.phone || s.sender_phone || "",
            sender_bank_account: s.bank_account || s.sender_bank_account || "",
            sender_logo_url: s.logo_url || "",
            due_date: dueDate.toISOString().split("T")[0],
            payment_terms: String(s.default_payment_terms || 14),
            payment_terms_days: s.default_payment_terms || 14,
            penalty_interest: s.default_penalty_interest ?? 7,
            items: [emptyItem(s.default_vat_percent || 25.5)],
          }));
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [id, isEdit]);

  // Autosave draft for new invoices (essential for "tab close → restore on login")
  useEffect(() => {
    if (isEdit) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY("new"), JSON.stringify(form));
      } catch {
        /* ignore */
      }
    }, 800);
    return () => clearTimeout(t);
  }, [form, isEdit]);

  // ============================ Helpers ============================
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = computeItem({ ...items[i], [field]: value });
    setForm({ ...form, items });
  };

  const addItem = () =>
    setForm({ ...form, items: [...form.items, emptyItem(settings.default_vat_percent || 25.5)] });

  const applyTemplateToRow = (idx, template) => {
    setForm((cur) => {
      const next = [...cur.items];
      const merged = computeItem({
        ...emptyItem(template.vat_percent ?? settings.default_vat_percent ?? 25.5),
        description: template.description || template.name,
        quantity: template.quantity ?? 1,
        unit: template.unit || "kpl",
        unit_price: template.unit_price ?? 0,
        vat_percent: template.vat_percent ?? 25.5,
        template_id: template.template_id,
      });
      next[idx] = merged;
      return { ...cur, items: next };
    });
  };

  const removeItem = (i) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, x) => x !== i) });
  };

  const subtotal = useMemo(
    () =>
      form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0),
    [form.items],
  );
  const vatTotal = useMemo(
    () =>
      form.items.reduce(
        (s, it) =>
          s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * ((Number(it.vat_percent) || 0) / 100),
        0,
      ),
    [form.items],
  );
  const totalAmount = subtotal + vatTotal;

  const barcodeData = useMemo(
    () => generateBarcodeData(form.sender_bank_account, totalAmount, form.reference_number, form.due_date),
    [form.sender_bank_account, totalAmount, form.reference_number, form.due_date],
  );

  // ============================ Save ============================
  const save = async (status) => {
    if (!form.recipient_name) {
      toast.error("Vastaanottajan nimi puuttuu");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        payment_terms_days: Number(form.payment_terms || form.payment_terms_days) || 14,
        payment_terms: String(form.payment_terms || form.payment_terms_days || 14),
        status: status || form.status,
      };
      // Uudelle laskulle: poista esikatselun numerot jotta backend laskee aina
      // tuoreet seuraavat numerot tietokannan tilan mukaan (estää tuplauksen,
      // jos useat välilehdet ovat auki samanaikaisesti).
      if (!isEdit) {
        delete payload.invoice_number;
        delete payload.reference_number;
      }
      let res;
      if (isEdit) res = await api.put(`/invoices/${id}`, payload);
      else res = await api.post("/invoices", payload);
      // Merkkaa käytetyt nimikkeet (telemetria nopeampaan järjestämiseen)
      const usedIds = (form.items || []).map((it) => it.template_id).filter(Boolean);
      if (usedIds.length > 0) {
        try { await api.post("/invoice-items/mark-used", { template_ids: usedIds }); } catch {}
      }
      toast.success(isEdit ? "Lasku päivitetty" : `Lasku #${res.data.invoice_number} luotu`);
      localStorage.removeItem(DRAFT_KEY("new"));
      navigate("/invoicing");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  // ============================ Document actions ============================
  // Build a snapshot resembling backend invoice (with totals + barcode) for PDF.
  const snapshot = () => ({
    ...form,
    subtotal,
    vat_total: vatTotal,
    total_amount: totalAmount,
    total: totalAmount,
    barcode_data: barcodeData,
  });
  const onPdf = () => {
    try {
      downloadInvoicePDF(snapshot(), settings);
      toast.success("PDF luotu — tarkista uusi välilehti tai latauksesi");
    } catch (e) {
      toast.error("PDF:n luonti epäonnistui: " + (e?.message || "tuntematon virhe"));
    }
  };
  const onPrint = () => {
    try {
      printInvoicePDF(snapshot(), settings);
      toast.success("Tulostusikkuna avautuu...");
    } catch (e) {
      toast.error("Tulostuksen avaaminen epäonnistui: " + (e?.message || "tuntematon virhe"));
    }
  };
  const onTxt = () => downloadInvoiceTXT(snapshot(), settings);

  const submitEmail = () => {
    const subj = encodeURIComponent(`Lasku #${form.invoice_number || "luonnos"}`);
    const body = encodeURIComponent(
      `Hyvä ${form.recipient_name},\n\nLähetämme oheisen laskun #${form.invoice_number || "luonnos"}.\n\nSumma: ${totalAmount.toFixed(2)} €\nEräpäivä: ${form.due_date}\nViitenumero: ${form.reference_number}\nTilinumero: ${form.sender_bank_account}\n\nYstävällisin terveisin,\n${form.sender_name}`,
    );
    window.location.href = `mailto:${emailTo}?subject=${subj}&body=${body}`;
    setEmailDialog(false);
  };

  // ============================ Render ============================
  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="invoice-form">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/invoicing")} data-testid="invoice-form-back">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-display font-bold text-amber-100">
            {isEdit ? "Muokkaa laskua" : "Uusi lasku"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {form.invoice_number ? `Lasku #${form.invoice_number}` : "Luonnos"} ·{" "}
            {form.reference_number ? `Viite: ${form.reference_number}` : "Viite luodaan tallennuksessa"}
          </p>
        </div>
        {!isEdit && form.invoice_number && (
          <div
            className="hidden md:block text-right px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30"
            data-testid="next-number-hint"
          >
            <p className="text-[10px] uppercase text-amber-300/80 tracking-wider">Auto-tunnistus tietokannasta</p>
            <p className="text-sm font-mono text-amber-100">
              Lasku → <strong>{form.invoice_number}</strong>
            </p>
            <p className="text-sm font-mono text-amber-100">
              Viite → <strong>{form.reference_number}</strong>
            </p>
          </div>
        )}
      </div>

      {/* Sender + Recipient */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Lähettäjä</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={form.sender_name || ""}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              placeholder="Yrityksen nimi"
              data-testid="sender-name"
            />
            <Input
              value={form.sender_business_id || ""}
              onChange={(e) => setForm({ ...form, sender_business_id: e.target.value })}
              placeholder="Y-tunnus"
            />
            <Input
              value={form.sender_address || ""}
              onChange={(e) => setForm({ ...form, sender_address: e.target.value })}
              placeholder="Osoite"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={form.sender_email || ""}
                onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                placeholder="Sähköposti"
              />
              <Input
                value={form.sender_phone || ""}
                onChange={(e) => setForm({ ...form, sender_phone: e.target.value })}
                placeholder="Puhelin"
              />
            </div>
            <Input
              value={form.sender_bank_account || ""}
              onChange={(e) => setForm({ ...form, sender_bank_account: e.target.value })}
              placeholder="IBAN-tilinumero"
              data-testid="sender-iban"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Vastaanottaja</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={form.recipient_name || ""}
              onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
              placeholder="Asiakkaan nimi"
              required
              data-testid="recipient-name"
            />
            <Input
              value={form.recipient_business_id || ""}
              onChange={(e) => setForm({ ...form, recipient_business_id: e.target.value })}
              placeholder="Y-tunnus"
            />
            <Input
              value={form.recipient_address || ""}
              onChange={(e) => setForm({ ...form, recipient_address: e.target.value })}
              placeholder="Osoite"
            />
            <Input
              value={form.recipient_email || ""}
              onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
              placeholder="Sähköposti"
            />
          </CardContent>
        </Card>
      </div>

      {/* Dates / terms */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Laskun päivämäärä</Label>
              <Input
                type="date"
                value={form.invoice_date || ""}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Eräpäivä</Label>
              <Input
                type="date"
                value={form.due_date || ""}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="due-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Maksuehto (pv)</Label>
              <Input
                value={form.payment_terms || ""}
                onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Viivästyskorko %</Label>
              <Input
                type="number"
                value={form.penalty_interest ?? 7}
                onChange={(e) => setForm({ ...form, penalty_interest: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">Rivit</CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPickerForRow("new")}
                className="gap-1 border-amber-500/30 text-amber-200"
                data-testid="pick-template-new"
              >
                <Package className="w-3 h-3" /> Valitse nimikkeestä
              </Button>
              <Button variant="outline" size="sm" onClick={addItem} className="gap-1" data-testid="add-item">
                <Plus className="w-3 h-3" /> Lisää tyhjä rivi
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.items.map((item, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`item-row-${i}`}>
              <div className="col-span-12 md:col-span-4">
                <Label className="text-[10px]">Kuvaus</Label>
                <Input
                  value={item.description}
                  onChange={(e) => updateItem(i, "description", e.target.value)}
                  placeholder="Tuote/palvelu"
                  data-testid={`item-description-${i}`}
                />
              </div>
              <div className="col-span-3 md:col-span-1">
                <Label className="text-[10px]">Määrä</Label>
                <Input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => updateItem(i, "quantity", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="col-span-3 md:col-span-1">
                <Label className="text-[10px]">Yksikkö</Label>
                <Input value={item.unit || "kpl"} onChange={(e) => updateItem(i, "unit", e.target.value)} />
              </div>
              <div className="col-span-3 md:col-span-2">
                <Label className="text-[10px]">Yksikköhinta</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={item.unit_price}
                  onChange={(e) => updateItem(i, "unit_price", parseFloat(e.target.value) || 0)}
                  data-testid={`item-price-${i}`}
                />
              </div>
              <div className="col-span-3 md:col-span-1">
                <Label className="text-[10px]">ALV %</Label>
                <Input
                  type="number"
                  value={item.vat_percent}
                  onChange={(e) => updateItem(i, "vat_percent", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="col-span-8 md:col-span-2">
                <Label className="text-[10px]">Yhteensä</Label>
                <Input value={(Number(item.total) || 0).toFixed(2) + " €"} readOnly className="bg-muted" />
              </div>
              <div className="col-span-4 md:col-span-1 flex justify-end items-center gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPickerForRow(i)}
                  className="text-amber-300 hover:text-amber-200"
                  title="Valitse nimikkeestä tähän riville"
                  data-testid={`pick-template-row-${i}`}
                >
                  <Package className="w-4 h-4" />
                </Button>
                <SaveAsTemplateButton row={item} />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeItem(i)}
                  disabled={form.items.length <= 1}
                  className="text-destructive"
                  data-testid={`remove-item-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}

          <div className="border-t pt-4 space-y-1 text-right">
            <p className="text-sm text-muted-foreground">
              Veroton: <span className="font-semibold text-foreground">{subtotal.toFixed(2)} €</span>
            </p>
            <p className="text-sm text-muted-foreground">
              ALV: <span className="font-semibold text-foreground">{vatTotal.toFixed(2)} €</span>
            </p>
            <p className="text-lg font-display font-bold" data-testid="invoice-total">
              Yhteensä: {totalAmount.toFixed(2)} €
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardContent className="p-4">
          <Label className="text-xs">Lisätiedot</Label>
          <Textarea
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Vapaavalintaiset lisätiedot..."
            className="mt-1"
          />
        </CardContent>
      </Card>

      {/* Preview */}
      {showPreview && (
        <div className="border border-border rounded-xl overflow-hidden shadow-lg bg-white">
          <div className="bg-zinc-100 px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-medium text-zinc-600">Laskun esikatselu — tulostusmuoto</span>
            <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)} className="h-7 gap-1 text-xs text-zinc-700">
              <EyeOff className="w-3 h-3" /> Sulje esikatselu
            </Button>
          </div>
          <div className="p-2 overflow-x-auto">
            <InvoicePreview
              form={form}
              subtotal={subtotal}
              vatTotal={vatTotal}
              totalAmount={totalAmount}
              barcodeData={barcodeData}
              settings={settings}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 flex-wrap" data-testid="invoice-form-actions">
        <Button variant="outline" onClick={() => navigate("/invoicing")}>
          Peruuta
        </Button>
        <Button variant="outline" onClick={() => setShowPreview((p) => !p)} className="gap-2" data-testid="action-preview">
          <Eye className="w-4 h-4" /> {showPreview ? "Piilota" : "Esikatsele lasku"}
        </Button>
        <Button variant="outline" onClick={onPdf} className="gap-2" data-testid="action-pdf">
          <Download className="w-4 h-4" /> Lataa PDF
        </Button>
        <Button variant="outline" onClick={onTxt} className="gap-2" data-testid="action-txt">
          <FileText className="w-4 h-4" /> TXT
        </Button>
        <Button variant="outline" onClick={onPrint} className="gap-2" data-testid="action-print">
          <Printer className="w-4 h-4" /> Tulosta
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setEmailTo(form.recipient_email || "");
            setEmailDialog(true);
          }}
          className="gap-2"
          data-testid="action-email"
        >
          <Mail className="w-4 h-4" /> Lähetä sähköpostiin
        </Button>
        <Button
          variant="secondary"
          onClick={() => save("draft")}
          disabled={saving}
          className="gap-2"
          data-testid="action-save-draft"
        >
          <Save className="w-4 h-4" /> Tallenna luonnos
        </Button>
        <Button
          onClick={() => save("sent")}
          disabled={saving}
          className="gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-medium"
          data-testid="action-save-send"
        >
          <Send className="w-4 h-4" /> Tallenna & Lähetä
        </Button>
      </div>

      {/* Email dialog */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Lähetä sähköpostiin</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Lasku #{form.invoice_number || "luonnos"} — {totalAmount.toFixed(2)} €
            </p>
            <div className="space-y-1">
              <Label className="text-xs">Vastaanottajan sähköposti</Label>
              <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="asiakas@email.fi" />
            </div>
            <p className="text-xs text-muted-foreground">
              Avaa sähköpostiohjelmasi esitäytetyllä viestillä. Voit liittää PDF-tiedoston ennen lähetystä.
            </p>
            <div className="flex gap-2">
              <Button onClick={submitEmail} disabled={!emailTo} className="flex-1 gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900">
                <Send className="w-4 h-4" /> Lähetä
              </Button>
              <Button variant="outline" onClick={() => setEmailDialog(false)}>
                Peruuta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item picker */}
      <InvoiceItemPicker
        open={pickerForRow !== null}
        onClose={() => setPickerForRow(null)}
        onSelect={(template) => {
          if (pickerForRow === "new") {
            // Lisää uusi rivi nimikkeen tiedoilla
            setForm((cur) => ({
              ...cur,
              items: [
                ...cur.items,
                computeItem({
                  ...emptyItem(template.vat_percent ?? settings.default_vat_percent ?? 25.5),
                  description: template.description || template.name,
                  quantity: template.quantity ?? 1,
                  unit: template.unit || "kpl",
                  unit_price: template.unit_price ?? 0,
                  vat_percent: template.vat_percent ?? 25.5,
                  template_id: template.template_id,
                }),
              ],
            }));
          } else if (typeof pickerForRow === "number") {
            applyTemplateToRow(pickerForRow, template);
          }
        }}
      />
    </div>
  );
}
